// src/pages/InitialConsultNote.jsx

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc,
  getDoc,
  setDoc,
  addDoc,
  collection,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { Save, ArrowLeft, CheckCircle, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import SmartCaptureModal from '../components/SmartCaptureModal';
import { apiPost } from '../utils/api'; // ✅ Unified backend helper

// --- Reusable Textarea ---
const NoteSectionTextarea = ({ label, ...props }) => (
  <div>
    <label className="block text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
      {label}
    </label>
    <textarea
      {...props}
      className="w-full h-56 bg-slate-100 dark:bg-zinc-800 border-none rounded-lg p-4 text-base text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
    />
  </div>
);

export default function InitialConsultNote() {
  const { patientId, noteId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [showSmartCapture, setShowSmartCapture] = useState(false);

  const [noteData, setNoteData] = useState({
    consultQuestion: '',
    hpi: '',
    exam: '',
    ap: '',
    type: 'Initial Consult',
  });

  const [patientName, setPatientName] = useState('');
  const [patientSex, setPatientSex] = useState('');
  const [patientAge, setPatientAge] = useState(null);

  const [activeTab, setActiveTab] = useState('Consult Q');
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isFixing, setIsFixing] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const noteTabs = ['Consult Q', 'HPI', 'Exam', 'A&P'];

  // ───────────────────────────────────────
  // Fetch Patient Context
  // ───────────────────────────────────────
  const fetchPatient = useCallback(async () => {
    if (!patientId) return;

    const ref = doc(db, 'patients', patientId);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const d = snap.data();
      setPatientName(`${d.firstName || ''} ${d.lastName || ''}`.trim());
      if (typeof d.sex === 'string') setPatientSex(d.sex);
      if (typeof d.age === 'number') setPatientAge(d.age);
    }
  }, [patientId]);

  useEffect(() => {
    const init = async () => {
      setErrorMsg('');
      fetchPatient();

      if (noteId && noteId !== 'new') {
        const ref = doc(db, 'notes', noteId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setNoteData(prev => ({ ...prev, ...snap.data() }));
        }
      }
    };

    init();
  }, [noteId, patientId, fetchPatient]);

  // ───────────────────────────────────────
  // Save Note
  // ───────────────────────────────────────
  const handleSave = async () => {
    setIsSaving(true);
    setErrorMsg('');

    const summary = `
Consult Question:
${noteData.consultQuestion}

HPI:
${noteData.hpi}

Exam:
${noteData.exam}

A&P:
${noteData.ap}
`;

    const payload = {
      ...noteData,
      summary,
      timestamp: serverTimestamp(),
      type: 'Initial Consult',
    };

    try {
      if (noteId && noteId !== 'new') {
        await setDoc(doc(db, 'notes', noteId), payload, { merge: true });
      } else {
        const ref = await addDoc(collection(db, 'notes'), {
          ...payload,
          ownerId: currentUser.uid,
          patientID: patientId,
        });
        navigate(`/initial-consult-note/${patientId}/${ref.id}`, {
          replace: true,
        });
      }

      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 1800);
    } catch (err) {
      setErrorMsg(err.message || 'Failed to save.');
    } finally {
      setIsSaving(false);
    }
  };

  // ───────────────────────────────────────
  // Fix HPI → /v1/format-hpi
  // ───────────────────────────────────────
  const handleFixHpi = async () => {
    setIsFixing('hpi');
    setErrorMsg('');

    try {
      const body = {
        hpi: noteData.hpi,
        patient_name: patientName,
        sex: patientSex,
        age: patientAge,
      };

      const result = await apiPost('/format-hpi', body);
      setNoteData(prev => ({
        ...prev,
        hpi: result.formatted_hpi || prev.hpi,
      }));
    } catch (err) {
      setErrorMsg(err.message || 'Fix HPI failed.');
    } finally {
      setIsFixing(null);
    }
  };

  // ───────────────────────────────────────
  // Format Exam → /v1/format-exam
  // ───────────────────────────────────────
  const handleFormatExam = async () => {
    setIsFixing('exam');
    setErrorMsg('');

    try {
      const result = await apiPost('/format-exam', {
        exam: noteData.exam,
      });

      setNoteData(prev => ({
        ...prev,
        exam: result.formatted_exam || prev.exam,
      }));
    } catch (err) {
      setErrorMsg(err.message || 'Formatting exam failed.');
    } finally {
      setIsFixing(null);
    }
  };

  // ───────────────────────────────────────
  // Generate A&P → /v1/generate-ap-v2
  // ───────────────────────────────────────
  const handleGenerateAP = async () => {
    setIsGenerating(true);
    setErrorMsg('');

    const context = `
Consult Question: ${noteData.consultQuestion}
HPI: ${noteData.hpi}
Exam: ${noteData.exam}
`;

    try {
      const result = await apiPost('/generate-ap-v2', {
        context: 'consult',
        current_note_context: context,
        consult_question: noteData.consultQuestion,
      });

      setNoteData(prev => ({
        ...prev,
        ap: result.summary || prev.ap,
      }));
    } catch (err) {
      setErrorMsg(err.message || 'A&P generation failed.');
    } finally {
      setIsGenerating(false);
    }
  };

  // ───────────────────────────────────────
  // Smart Capture → /v1/format-consult-text
  // ───────────────────────────────────────
  const handleSmartCaptureSubmit = async text => {
    try {
      const result = await apiPost('/format-consult-text', { text });

      setNoteData(prev => ({
        ...prev,
        consultQuestion: result.consult_question || prev.consultQuestion,
        hpi: result.hpi || prev.hpi,
        exam: result.exam || prev.exam,
        ap: result.assessment_plan || prev.ap,
      }));
    } catch (err) {
      console.error('Smart capture failed:', err);
      setErrorMsg(err.message || 'Smart Capture formatting failed.');
    }
  };

  // ───────────────────────────────────────
  // Render
  // ───────────────────────────────────────
  return (
    <div className="bg-[#f4f7f9] dark:bg-black min-h-screen">
      <Header />

      <main className="max-w-5xl mx-auto py-10 px-4">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl">
          <div className="p-6 border-b border-gray-200 dark:border-zinc-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate(-1)}
                  className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-zinc-800"
                >
                  <ArrowLeft size={20} />
                </button>

                <div>
                  <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">
                    Initial Consult
                  </h1>
                  <p className="text-gray-500 dark:text-gray-400">
                    for {patientName || 'patient'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowSmartCapture(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-full font-semibold hover:bg-purple-600 transition"
                >
                  <Sparkles size={16} /> Smart Capture
                </button>

                <button
                  onClick={handleSave}
                  disabled={isSaving || isSaved}
                  className={`flex items-center justify-center gap-2 px-8 py-2 rounded-full font-semibold text-white transition-all duration-300 ${
                    isSaved
                      ? 'bg-green-500'
                      : 'bg-blue-500 hover:bg-blue-600'
                  }`}
                >
                  {isSaving ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : isSaved ? (
                    <>
                      <CheckCircle size={18} /> Saved!
                    </>
                  ) : (
                    <>
                      <Save size={18} /> Save Note
                    </>
                  )}
                </button>
              </div>
            </div>

            {errorMsg && (
              <div className="mt-2 text-sm text-red-600 dark:text-red-400">
                {errorMsg}
              </div>
            )}
          </div>

          <div className="p-6">
            {/* Tabs */}
            <div className="flex justify-center mb-8">
              <div className="flex items-center space-x-2 bg-slate-200/70 dark:bg-zinc-800 rounded-full p-1">
                {noteTabs.map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className="relative px-5 py-1.5 text-sm font-semibold transition-colors duration-300"
                  >
                    <span
                      className={`relative z-10 ${
                        activeTab === tab
                          ? 'text-gray-800 dark:text-white'
                          : 'text-gray-500 hover:text-gray-800 dark:hover:text-white'
                      }`}
                    >
                      {tab}
                    </span>

                    {activeTab === tab && (
                      <motion.div
                        layoutId="active-pill"
                        className="absolute inset-0 bg-white dark:bg-zinc-700 rounded-full shadow-sm"
                        transition={{
                          type: 'spring',
                          stiffness: 350,
                          damping: 30,
                        }}
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Consult Question */}
            {activeTab === 'Consult Q' && (
              <NoteSectionTextarea
                label="Consult Question"
                name="consultQuestion"
                value={noteData.consultQuestion}
                onChange={e =>
                  setNoteData(prev => ({
                    ...prev,
                    consultQuestion: e.target.value,
                  }))
                }
              />
            )}

            {/* HPI */}
            {activeTab === 'HPI' && (
              <div className="space-y-3">
                <NoteSectionTextarea
                  label="History of Present Illness"
                  name="hpi"
                  value={noteData.hpi}
                  onChange={e =>
                    setNoteData(prev => ({
                      ...prev,
                      hpi: e.target.value,
                    }))
                  }
                />

                <div className="flex justify-end">
                  <button
                    onClick={handleFixHpi}
                    disabled={isFixing === 'hpi'}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-full font-semibold hover:bg-indigo-700 transition"
                  >
                    {isFixing === 'hpi' ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      '✨ Fix HPI'
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Exam */}
            {activeTab === 'Exam' && (
              <div className="space-y-3">
                <NoteSectionTextarea
                  label="Focused Physical Exam"
                  name="exam"
                  value={noteData.exam}
                  onChange={e =>
                    setNoteData(prev => ({
                      ...prev,
                      exam: e.target.value,
                    }))
                  }
                />

                <div className="flex justify-end">
                  <button
                    onClick={handleFormatExam}
                    disabled={isFixing === 'exam'}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition"
                  >
                    {isFixing === 'exam' ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      '🩺 Format Exam'
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* A&P */}
            {activeTab === 'A&P' && (
              <div className="space-y-4">
                <NoteSectionTextarea
                  label="Assessment & Plan"
                  name="ap"
                  value={noteData.ap}
                  onChange={e =>
                    setNoteData(prev => ({
                      ...prev,
                      ap: e.target.value,
                    }))
                  }
                />

                <button
                  onClick={handleGenerateAP}
                  disabled={isGenerating}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-full font-semibold hover:bg-purple-600 transition"
                >
                  {isGenerating ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Sparkles size={16} /> Generate A&P
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Smart Capture */}
      <SmartCaptureModal
        show={showSmartCapture}
        onClose={() => setShowSmartCapture(false)}
        onTextExtracted={handleSmartCaptureSubmit}
      />
    </div>
  );
}
